import type { StrippedUser } from 'common/api/endpoints/users';
import type { IdeaStatus, StatusType } from 'common/api/resources/statuses';
import type { MimeTypeValues } from 'common/constants/files';
import type { FieldDisplayMode, IdeaFieldTypes } from 'common/constants/ideas';
import type { IdeaScoreFieldTypes } from 'common/constants/ideaScoreFields';
import type { Source } from 'common/constants/sources';
import type { CalculationFunctions } from 'common/prioritization/CalculationFactorTypes';
import type { CustomIdeaFieldTypes, Fields } from 'common/types/ideas';

export type Idea = {
  _id: string;
  ancestorIDs: string[];
  childCount: number;
  companyID: string;
  created: string;
  deleted: boolean;
  description: string;
  fields: Fields; // default & custom fields
  files: { mimeType: MimeTypeValues; name: string; url: string }[];
  groupID: string | null;
  groupName: string | null;
  groupSortKey: number;
  imageURLs: string[];
  ownerID: string | null;
  parent: Pick<Idea, 'urlName' | 'title'> | null;
  parentID: string | null;
  source: Source;
  sortKeys: Record<string, string | number | Date>;
  statusID: string;
  title: string;
  urlName: string;
  users: Record<string, StrippedUser>;
};

export type IdeaIDOnly = Pick<Idea, '_id'>;

export type IdeaLight = {
  _id: string;
  title: string;
  urlName: string;
  statusID: string;
};

interface BaseIdeaField {
  name: string;
  label: string;
  immutable?: boolean;
  deletable?: boolean;
}

export interface DefaultIdeaField extends BaseIdeaField {
  type: Exclude<IdeaFieldTypes, IdeaFieldTypes.formula | IdeaFieldTypes.score>;
  custom: false;
  options?: string[];
  defaultSortKey?: number | string;
}

export interface CustomIdeaField extends BaseIdeaField {
  type: CustomIdeaFieldTypes;
  custom: true;
  options?: string[];
  displayMode?: FieldDisplayMode;
}

export interface FormulaIdeaField extends BaseIdeaField {
  type: IdeaFieldTypes.formula;
  custom: true;
  factor: string | null;
  fieldID: string | null;
  segmentID: string | null;
  calculationFunction: CalculationFunctions;
  displayMode?: FieldDisplayMode;
}

export interface ScoreIdeaField extends BaseIdeaField {
  type: IdeaFieldTypes.score;
  custom: true;
  fields: Array<{ fieldID: string; weight: number; type: IdeaScoreFieldTypes }>;
  displayMode?: FieldDisplayMode;
}

export type IdeaField = DefaultIdeaField | CustomIdeaField | FormulaIdeaField | ScoreIdeaField;

export type IdeaTheme = {
  _id: string;
  autopilotEnabled: boolean;
  created: string; // ISO string
  createdByAutopilot: boolean;
  description: string;
  name: string;
};

// returned by IdeasConfigurationEndpoint
export type IdeasConfiguration = {
  fields: IdeaField[];
  statuses: IdeaStatus[];
  themes: IdeaTheme[];
};

// returned by GetIdeaImpactEndpoint
export type IdeaImpact = {
  name: string;
  value: number;
};

export { StatusType };

// CreateStatusEndpoint, UpdateStatusEndpoint
export type StatusResponse = {
  status: IdeaStatus;
};

// CreateIdeaEndpoint, UpdateIdeaEndpoint
export type IdeaResponse = {
  idea: Idea;
};

export enum AutogroupIdeasStatus {
  notRunning = 'not running',
  running = 'running',
  completed = 'completed',
  failed = 'failed',
}

export type UpdateIdeaFields = Record<string, string | string[] | number | boolean | null | Date>;

export type CreateIdeaData = {
  title: string;
  description?: string;
  groupID?: string | null;
  parentID?: string | null;
  details?: string;
  fields?: UpdateIdeaFields;
};

export type CreateIdeaFromInsightData = {
  insightID: string;
  title: string;
};

// if you update this, also update UpdateIdeaEndpoint
export type UpdateIdeaData = {
  description?: string;
  fields?: UpdateIdeaFields;
  fileURLs?: string;
  ideaID: string;
  imageURLs?: string;
  title?: string;
};

export type MergeIdeaRequest = {
  mergeIdeaID: string;
  mergeIntoIdeaID: string;
};

export type MergeIdeaData = {
  mergeIdea: Idea;
  mergeIntoIdea: Idea;
};

export type UnmergeIdeaData = {
  ideaID: string;
};

export type MoveIdeaData = {
  ideaID: string;
  groupID?: string | null;
  parentID?: string | null;
};

export enum BulkEditIdeaOperations {
  merge = 'merge',
  move = 'move',
  delete = 'delete',
  updateOwner = 'updateOwner',
  updateStatus = 'updateStatus',
}

export type BulkMergeIdeaRequest = {
  mergeIntoIdeaID: string;
};

export type BulkMoveIdeaRequest = {
  groupID: string | null;
  parentID: string | null;
};

export type BulkUpdateOwnerIdeaRequest = {
  ownerID: string | null;
};

export type BulkUpdateStatusIdeaRequest = {
  statusID: string;
};

export type BulkEditIdeaRequest = {
  ideaIDs: string[];
  operationType: BulkEditIdeaOperations;
};

export type BulkMergeData = BulkEditIdeaRequest &
  BulkMergeIdeaRequest & { operationType: BulkEditIdeaOperations.merge };
export type BulkMoveData = BulkEditIdeaRequest &
  BulkMoveIdeaRequest & { operationType: BulkEditIdeaOperations.move };
export type BulkUpdateStatusData = BulkEditIdeaRequest &
  BulkUpdateStatusIdeaRequest & { operationType: BulkEditIdeaOperations.updateStatus };
export type BulkUpdateOwnerData = BulkEditIdeaRequest &
  BulkUpdateOwnerIdeaRequest & { operationType: BulkEditIdeaOperations.updateOwner };
export type BulkDeleteData = BulkEditIdeaRequest & { operationType: BulkEditIdeaOperations.delete };

export type IdeasBetaToggleRequest = {
  enabled: boolean;
  reason?: string;
};

export type AutogroupIdeasStatusResponse = {
  status: AutogroupIdeasStatus;
};
